import { createContext, useEffect, useState } from "react";
import { getFirestore, collection, query, onSnapshot, orderBy, deleteDoc, doc, addDoc, updateDoc, getDoc, startAfter, limit, getDocs, endAt, startAt } from "firebase/firestore";
import { app } from "../fb/config";
import { IBusiness } from "../interfaces/IBusiness";
import { IDoc } from "./MaintenanceContext";
import { normalizeString } from "../utils/functions";

export interface IBusinessProps {
  business: IBusiness[];
  loading: boolean;
  deleteBusiness: (businessToDelete: IBusiness) => Promise<void>;
  createBusiness: (doc: any) => Promise<void>;
  updateBusiness: (data: IBusiness) => Promise<void>;
  deleteBusinessTickets: (ticketsToDelete: IDoc[]) => Promise<void>;
  getBusinessById: (id: string) => Promise<IBusiness | null>;
  getFirebaseInitialBusiness: () => Promise<IBusiness[]>;
  getAllBusinessByName: (nameInit: string) => Promise<IBusiness[]>;
  getFirebaseNextBusiness: (lastNameToFilter: string) => Promise<IBusiness[]>;
  createCategory: (nameToShow: string) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  categories: ICategory[];
}

export interface ICategory {
  nameToShow: string;
  id: string;
}

export const BusinessContext = createContext<IBusinessProps>(null);

export const BusinessProvider: any = ({ children }: any) => {
  const [business, setBusiness] = useState<IBusiness[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setLoading] = useState(true);

  const db = getFirestore(app);

  // 🔹 Suscripción en tiempo real a categorías
  useEffect(() => {
    const q = query(collection(db, "categories"), orderBy("nameToShow", "asc"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const arr: ICategory[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          nameToShow: data.nameToShow ?? "", // 👈 aseguramos la propiedad
        };
      });
      setCategories(arr);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [db]);

  const getFirebaseInitialBusiness = async () => {
    const q = query(collection(db, "business"), orderBy("nameToFilter", "asc"), limit(30));
    const snap = await getDocs(q);
    const docs = snap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as IBusiness[];
    setBusiness(docs);
    return docs;
  };

  const getFirebaseNextBusiness = async (lastNameToFilter: string) => {
    const q = query(collection(db, "business"), orderBy("nameToFilter", "asc"), startAfter(lastNameToFilter), limit(25));
    const snap = await getDocs(q);
    const docs = snap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as IBusiness[];
    setBusiness((prev) => [...prev, ...docs]);
    return docs;
  };

  const getAllBusinessByName = async (nameInit: string) => {
    const q = query(collection(db, "business"), orderBy("nameToFilter", "asc"), startAt(nameInit), endAt(nameInit + "\uf8ff"), limit(25));
    const snap = await getDocs(q);
    const docs = snap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as IBusiness[];
    setBusiness(docs);
    return docs;
  };

  // 🔹 CRUD básico
  const getBusinessById = async (idBusiness: string) => {
    try {
      const q = doc(db, "business", idBusiness);
      const docSnap = await getDoc(q);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as IBusiness;
      }
      console.warn(`Business not found: ${idBusiness}`);
      return null;
    } catch (error) {
      console.error(`Error fetching business ${idBusiness}:`, error);
      return null;
    }
  };

  const deleteBusiness = async (businessToDelete: IBusiness) => {
    await deleteDoc(doc(db, "business", businessToDelete.id));
  };

  const createBusiness = async (docData: any) => {
    await addDoc(collection(db, "business"), {
      name: docData.name,
      nameToFilter: normalizeString(docData.name),
      nif: docData.nif,
      description: docData?.description ?? "",
      logo: docData?.logo ?? "",
      locals: docData.locals,
      showInBMC: docData.showInBMC,
    });
  };

  const updateBusiness = async (data: IBusiness) => {
    await updateDoc(doc(db, "business", data.id), {
      ...data,
    });
  };

  // 🔹 Categorías
  const deleteCategory = async (id: string) => {
    await deleteDoc(doc(db, "categories", id));
  };

  const createCategory = async (nameToShow: string) => {
    await addDoc(collection(db, "categories"), { nameToShow });
  };

  // 🔹 Tickets relacionados
  const deleteBusinessTickets = async (ticketsToDelete: IDoc[]) => {
    await Promise.all(ticketsToDelete.map((ticket) => deleteDoc(doc(db, "tickets", ticket.id))));
  };

  return (
    <BusinessContext.Provider
      value={{
        business,
        deleteBusiness,
        createBusiness,
        updateBusiness,
        deleteBusinessTickets,
        loading,
        getBusinessById,
        getAllBusinessByName,
        getFirebaseNextBusiness,
        getFirebaseInitialBusiness,
        createCategory,
        deleteCategory,
        categories,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};
